import { useEffect, useRef } from 'react';
import { Eraser } from 'lucide-react';

/**
 * Zone de signature, à la souris ou au doigt. Rend un PNG en data URL à chaque
 * trait terminé ('' une fois effacée) — c'est ce PNG qui part sur le PDF.
 */
export default function SignaturePad({ value, onChange }: { value: string; onChange: (png: string) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const dessine = useRef(false);
  const vide = useRef(!value);
  const dernier = useRef<string>(value);

  // Échelle écran haute densité, et redessin de la signature déjà enregistrée.
  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const { width, height } = c.getBoundingClientRect();
    c.width = width * ratio;
    c.height = height * ratio;
    const ctx = c.getContext('2d')!;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e3a5f';
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, width, height);
      img.src = value;
    }
    // Monté une seule fois : les traits suivants vivent dans le canvas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effacé depuis l'extérieur (ou fiche rechargée) : on suit.
  useEffect(() => {
    if (value === dernier.current) return;
    dernier.current = value;
    if (!value) {
      const c = canvas.current;
      c?.getContext('2d')?.clearRect(0, 0, c.width, c.height);
      vide.current = true;
    }
  }, [value]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const debut = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const ctx = e.currentTarget.getContext('2d')!;
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.stroke();
    dessine.current = true;
    vide.current = false;
  };

  const trace = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dessine.current) return;
    const ctx = e.currentTarget.getContext('2d')!;
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const fin = () => {
    if (!dessine.current) return;
    dessine.current = false;
    const png = vide.current ? '' : canvas.current!.toDataURL('image/png');
    dernier.current = png;
    onChange(png);
  };

  const effacer = () => {
    const c = canvas.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    vide.current = true;
    dernier.current = '';
    onChange('');
  };

  return (
    <div>
      <div className="relative rounded-lg border-2 border-dashed border-slate-300 bg-white hover:border-emerald-400 transition-colors">
        <canvas
          ref={canvas}
          className="block h-36 w-full touch-none cursor-crosshair"
          onPointerDown={debut}
          onPointerMove={trace}
          onPointerUp={fin}
          onPointerLeave={fin}
        />
        {!value && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[12px] text-slate-400">
            Signez ici, à la souris ou au doigt
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={effacer}
        className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-800"
      >
        <Eraser size={12} /> Effacer la signature
      </button>
    </div>
  );
}
